<template>
  <div class="row purchases">
    <div class="col-lg-10 offset-lg-1">
      <div class="mb-2 mt-3">
        <a href="https://www.beekeeperstudio.io/pricing">&lt; back to pricing</a>
      </div>
      <div class="card rounded-3">
        <div class="row g-0">
          <!-- LEFT: Settings -->
          <div class="col-lg-5">
            <div class="card-body p-md-4">
              <h3 class="card-title mb-3 text-center">Generate a Quote</h3>
              <p class="text-muted text-small text-center">Annual license pricing</p>

              <div v-if="errorMessage" class="alert alert-danger">
                <i class="bi bi-exclamation-circle-fill"></i> {{ errorMessage }}
              </div>

              <form @submit.prevent="submitQuote" ref="quoteForm">

                <!-- ===== LICENSE CONFIGURATION ===== -->
                <h6 class="text-muted text-uppercase small mb-2 mt-2">License Configuration</h6>

                <div class="mb-3">
                  <label for="plan_key" class="form-label">Plan</label>
                  <select id="plan_key" class="form-select"
                    v-model="selectedPlan">
                    <option v-for="plan in availablePlans" :key="plan.key" :value="plan.key">
                      {{ plan.name }} - ${{ formatPrice(plan.unit_amount) }}/seat/year
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label for="quantity" class="form-label">Number of Licenses</label>
                  <input type="number" class="form-control" id="quantity"
                    min="1" :max="currentMaxQuantity || 9999"
                    v-model.number="quantity">
                  <small v-if="currentMaxQuantity" class="text-muted">
                    Maximum {{ currentMaxQuantity }} seats for {{ currentPlanName }}.
                  </small>
                </div>

                <!-- Net terms (only for 10+ licenses) -->
                <div class="mb-3" v-if="quantity >= 10">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="net_terms"
                      v-model="netTerms">
                    <label class="form-check-label" for="net_terms">Request Net 30 payment terms</label>
                  </div>
                  <small class="text-muted">We'll send an invoice instead of requiring online payment.</small>
                </div>

                <div class="alert alert-secondary small" v-if="quantity < 10">
                  <i class="bi bi-info-circle"></i> Purchases of fewer than 10 licenses are self-service online only.
                </div>

                <hr>

                <!-- ===== CUSTOMER DETAILS ===== -->
                <h6 class="text-muted text-uppercase small mb-2">Customer Details</h6>

                <div class="mb-3">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="is_reseller"
                      v-model="isReseller" @change="onResellerChanged">
                    <label class="form-check-label" for="is_reseller">I am a reseller purchasing on behalf of a customer</label>
                  </div>
                </div>

                <div class="mb-3">
                  <label for="company_name" class="form-label">{{ isReseller ? 'Reseller Company Name' : 'Company Name' }}</label>
                  <input type="text" class="form-control" id="company_name"
                    v-model="companyName" :placeholder="isReseller ? 'Your reseller company' : 'Acme Corp'">
                </div>

                <div class="mb-3">
                  <label for="contact_name" class="form-label">Contact Name</label>
                  <input type="text" class="form-control" id="contact_name"
                    v-model="contactName" placeholder="Jane Smith">
                </div>

                <div class="mb-3">
                  <label for="contact_email" class="form-label">Contact Email <span class="text-danger">*</span></label>
                  <input type="email" class="form-control" id="contact_email"
                    required v-model="contactEmail" placeholder="you@company.com">
                </div>

                <!-- Reseller-specific fields -->
                <template v-if="isReseller">
                  <div class="card bg-dark bg-opacity-25 p-3 mb-3">
                    <h6 class="small text-muted text-uppercase mb-2">End Customer</h6>

                    <div class="mb-2">
                      <label for="end_customer_company" class="form-label small mb-1">End Customer Company</label>
                      <input type="text" class="form-control form-control-sm" id="end_customer_company"
                        v-model="endCustomerCompany" placeholder="Customer's company name">
                    </div>

                    <div>
                      <label for="end_customer_name" class="form-label small mb-1">End Customer Contact</label>
                      <input type="text" class="form-control form-control-sm" id="end_customer_name"
                        v-model="endCustomerName" placeholder="Customer's contact name">
                    </div>
                  </div>
                </template>

                <hr>

                <!-- ===== OTHER DETAILS ===== -->
                <h6 class="text-muted text-uppercase small mb-2">Other Details</h6>

                <div class="mb-3">
                  <label for="other_details" class="form-label">Address, PO number, or other notes</label>
                  <textarea class="form-control" id="other_details" rows="3"
                    v-model="otherDetails" placeholder="Billing address, PO number, special instructions, etc."></textarea>
                </div>

                <div class="mt-3 d-grid gap-2">
                  <button class="btn btn-block btn-info" type="submit" :disabled="!canGenerate || submitting">
                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i v-else class="bi bi-file-earmark-pdf"></i>
                    {{ submitting ? 'Generating...' : 'Generate Quote' }}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- RIGHT: Paper-style preview -->
          <div class="col-lg-7 d-flex align-items-start justify-content-center py-4 px-3" style="background-color: #e9ecef;">
            <div class="quote-paper">
              <!-- Header -->
              <div class="quote-header">
                <div>
                  <div class="quote-business-name">Beekeeper Studio, Inc</div>
                  <div class="quote-muted">PO Box 140565, Dallas, TX 75214, USA</div>
                  <div class="quote-muted">support@beekeeperstudio.io</div>
                </div>
              </div>

              <hr class="my-3">

              <div class="quote-title">QUOTE</div>

              <!-- Meta -->
              <table class="quote-meta-table">
                <tbody>
                  <tr>
                    <td class="quote-meta-label">Quote Number:</td>
                    <td>Assigned on generation</td>
                  </tr>
                  <tr>
                    <td class="quote-meta-label">Date:</td>
                    <td>{{ todayFormatted }}</td>
                  </tr>
                  <tr>
                    <td class="quote-meta-label">Valid Until:</td>
                    <td>{{ validUntilFormatted }}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Recipient -->
              <div class="mt-3">
                <div class="quote-section-label">Prepared For:</div>
                <template v-if="companyName || contactName || contactEmail">
                  <div v-if="companyName" class="fw-bold">{{ companyName }}</div>
                  <div v-if="contactName">{{ contactName }}</div>
                  <div v-if="contactEmail">{{ contactEmail }}</div>
                </template>
                <div v-else class="quote-muted fst-italic">Enter contact details on the left</div>

                <template v-if="isReseller">
                  <div class="quote-muted fst-italic mt-1">Reseller</div>
                  <template v-if="endCustomerCompany || endCustomerName">
                    <div class="mt-1">
                      <span class="quote-section-label">End Customer:</span>
                    </div>
                    <div v-if="endCustomerCompany">{{ endCustomerCompany }}</div>
                    <div v-if="endCustomerName">{{ endCustomerName }}</div>
                  </template>
                </template>

                <div v-if="otherDetails" class="mt-2 quote-muted" style="white-space: pre-line; font-size: 0.6rem;">{{ otherDetails }}</div>
              </div>

              <!-- Line items table -->
              <table class="quote-line-items mt-3">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th class="text-center">Qty</th>
                    <th class="text-end">Unit Price</th>
                    <th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Beekeeper Studio {{ currentPlanName }} License (Annual)</td>
                    <td class="text-center">{{ quantity }}</td>
                    <td class="text-end">${{ formatPrice(unitPriceCents) }} / seat / year</td>
                    <td class="text-end">${{ formatPrice(totalCents) }} / year</td>
                  </tr>
                </tbody>
              </table>

              <!-- Total -->
              <div class="text-end mt-2 mb-3">
                <strong>Annual Total: ${{ formatPrice(totalCents) }}</strong>
              </div>

              <!-- Terms -->
              <div class="mt-3">
                <div class="fw-bold mb-1" style="font-size: 0.8rem;">Terms &amp; Conditions</div>
                <hr class="my-1">
                <ul class="quote-terms">
                  <li>This quote is valid for 30 days from the date of issue (valid until {{ validUntilFormatted }}).</li>
                  <li>Subscriptions auto-renew annually at the then-current price. You can cancel anytime before your renewal date.</li>
                  <li>All prices are in USD and exclude applicable taxes. Tax will be calculated at checkout.</li>
                  <li v-if="netTerms && quantity >= 10">Payment Terms: Net 30 days from invoice date. Contact sales@beekeeperstudio.io to proceed with this order.</li>
                  <li v-else-if="quantity < 10">This is a self-service purchase. Complete your order online at app.beekeeperstudio.io/purchases/new</li>
                  <li v-else>To purchase, visit app.beekeeperstudio.io/purchases/new or contact sales@beekeeperstudio.io to arrange invoicing.</li>
                  <li>If the self-service price changes before you purchase, email support@beekeeperstudio.io with your quote number and we'll honor the quoted price.</li>
                </ul>
              </div>

              <!-- Footer -->
              <hr class="mt-3 mb-2">
              <div class="text-center quote-muted" style="font-size: 0.6rem;">
                Beekeeper Studio, Inc | PO Box 140565, Dallas, TX 75214, USA | support@beekeeperstudio.io
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer links -->
      <div class="row mt-5 mb-3">
        <div class="col-12">
          <hr class="mb-4">
          <div class="text-center small text-muted">
            <p class="mb-2">Ready to buy? <a :href="purchasePath">Purchase directly online</a> - no quote needed for self-service.</p>
            <div>
              <a target="_blank" href="https://beekeeperstudio.io/pricing">Pricing</a> &middot;
              <a target="_blank" href="https://beekeeperstudio.io/pricing#salesfaq">Sales FAQ</a> &middot;
              <a href="mailto:sales@beekeeperstudio.io">Contact Sales</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['prices', 'createPath', 'purchasePath'],
  data() {
    return {
      isReseller: false,
      selectedPlan: 'professional',
      quantity: 1,
      contactEmail: '',
      contactName: '',
      companyName: '',
      endCustomerName: '',
      endCustomerCompany: '',
      otherDetails: '',
      netTerms: false,
      submitting: false,
      errorMessage: null
    }
  },
  computed: {
    parsedPrices() {
      try {
        return typeof this.prices === 'string' ? JSON.parse(this.prices) : this.prices
      } catch {
        return {}
      }
    },
    availablePlans() {
      const plans = []
      for (const [key, data] of Object.entries(this.parsedPrices)) {
        plans.push({ key, ...data })
      }
      return plans
    },
    currentPriceData() {
      return this.parsedPrices[this.selectedPlan]
    },
    currentPlanName() {
      return this.currentPriceData?.name || this.selectedPlan
    },
    currentMaxQuantity() {
      return this.currentPriceData?.max_quantity || null
    },
    unitPriceCents() {
      return this.currentPriceData?.unit_amount || 0
    },
    totalCents() {
      return this.unitPriceCents * this.quantity
    },
    todayFormatted() {
      return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    validUntilFormatted() {
      const date = new Date()
      date.setDate(date.getDate() + 30)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    csrfToken() {
      const meta = document.querySelector('meta[name=csrf-token]')
      return meta ? meta.getAttribute('content') : ''
    },
    canGenerate() {
      return this.contactEmail && this.quantity > 0 && this.currentPriceData
    }
  },
  watch: {
    quantity(val) {
      if (val < 10) {
        this.netTerms = false
      }
      if (this.currentMaxQuantity && val > this.currentMaxQuantity) {
        this.quantity = this.currentMaxQuantity
      }
    }
  },
  methods: {
    onResellerChanged() {
    },
    formatPrice(cents) {
      return (cents / 100).toFixed(2)
    },
    async submitQuote() {
      this.submitting = true
      this.errorMessage = null

      try {
        const payload = {
          generated_quote: {
            plan_key: this.selectedPlan,
            quantity: this.quantity,
            contact_email: this.contactEmail,
            contact_name: this.contactName,
            company_name: this.companyName,
            is_reseller: this.isReseller ? '1' : '0',
            net_terms: this.netTerms ? '1' : '0',
            end_customer_name: this.endCustomerName,
            end_customer_company: this.endCustomerCompany,
            other_details: this.otherDetails
          }
        }

        const response = await fetch(this.createPath, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify(payload)
        })

        const data = await response.json()

        if (response.ok && data.redirect_url) {
          window.location.href = data.redirect_url
        } else if (data.errors) {
          this.errorMessage = data.errors.join('. ')
        } else {
          this.errorMessage = 'Something went wrong. Please try again.'
        }
      } catch (error) {
        this.errorMessage = `Network error: ${error.message}`
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.quote-paper {
  background: #fff;
  color: #333;
  padding: 32px 36px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  border-radius: 2px;
  font-size: 0.75rem;
  line-height: 1.5;
}

.quote-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.quote-business-name {
  font-size: 1.1rem;
  font-weight: bold;
  color: #222;
}

.quote-muted {
  color: #888;
  font-size: 0.65rem;
}

.quote-title {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.quote-meta-table {
  font-size: 0.7rem;
}

.quote-meta-table td {
  padding: 1px 8px 1px 0;
}

.quote-meta-label {
  font-weight: bold;
  white-space: nowrap;
}

.quote-section-label {
  font-size: 0.65rem;
  font-weight: bold;
  color: #888;
  margin-bottom: 2px;
}

.quote-line-items {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
}

.quote-line-items thead tr {
  background: #333;
  color: #fff;
}

.quote-line-items th,
.quote-line-items td {
  padding: 6px 8px;
}

.quote-line-items tbody tr {
  background: #f5f5f5;
}

.quote-terms {
  font-size: 0.6rem;
  color: #555;
  padding-left: 16px;
  margin-bottom: 0;
}

.quote-terms li {
  margin-bottom: 3px;
}
</style>
